import { useEffect, useState, useCallback } from 'react';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  Search, ChevronDown, ChevronRight, Database, Eye, FileText
} from 'lucide-react';
import { TENANT } from '../../config';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface AmlStats {
  entities: Record<string, number>;
  screeningsLast30Days: Record<string, number>;
  pendingAlerts: number;
}

interface AmlAlert {
  id: string;
  screening_type: string;
  input_full_name: string | null;
  input_first_name: string | null;
  input_last_name: string | null;
  input_dob: string | null;
  input_country: string | null;
  match_count: number;
  highest_score: string;
  status: 'review' | 'blocked';
  screened_at: string;
  user_email: string | null;
}

interface AmlSource {
  source_id: string;
  name: string;
  category: string;
  format: string;
  entity_count: number;
  last_fetched_at: string | null;
  last_parsed_at: string | null;
  last_status: string | null;
  last_error: string | null;
  fetch_interval_hours: number;
  is_active: boolean;
}

interface SarFiling {
  id: string;
  screening_id: string;
  subject_name: string;
  activity_type: string;
  summary: string;
  amount_involved: string | null;
  currency: string;
  activity_date: string | null;
  status: 'draft' | 'submitted' | 'acknowledged' | 'closed';
  reference_number: string | null;
  submitted_at: string | null;
  filer_email: string;
  subject_email: string | null;
  screening_status: string;
  highest_score: string;
  created_at: string;
}

interface ScreeningDetail {
  screening: Record<string, unknown>;
  matches: Array<{
    id: string;
    matched_name: string;
    name_score: string;
    dob_score: string;
    country_score: string;
    composite_score: string;
    category: string;
    list_name: string;
    entity_type: string;
    programs: string[];
    nationalities: string[];
    source_id: string;
    is_false_positive: boolean;
    all_names: string[];
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const API = '/api/v1';
const token = () => localStorage.getItem('authToken') || '';

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...opts.headers },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function scoreBadge(score: number) {
  const pct = Math.round(score * 100);
  const cls = score >= 0.85 ? 'bg-red-100 text-red-700'
            : score >= 0.70 ? 'bg-yellow-100 text-yellow-700'
            : 'bg-green-100 text-green-700';
  return <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${cls}`}>{pct}%</span>;
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    blocked: 'bg-red-100 text-red-700',
    review:  'bg-yellow-100 text-yellow-700',
    clear:   'bg-green-100 text-green-700',
    running: 'bg-blue-100 text-blue-700',
    success: 'bg-green-100 text-green-700',
    error:   'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function formatDate(s: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-views
// ─────────────────────────────────────────────────────────────────────────────

function AlertsTab() {
  const [alerts, setAlerts]     = useState<AmlAlert[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail]     = useState<ScreeningDetail | null>(null);
  const [reviewing, setReviewing]   = useState(false);
  const [decision, setDecision]     = useState('false_positive');
  const [notes, setNotes]           = useState('');
  const [sarActivity, setSarActivity] = useState('money_laundering');
  const [sarSummary, setSarSummary]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ alerts: AmlAlert[] }>('/aml/alerts?limit=100');
      setAlerts(data.alerts);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function expand(id: string) {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id);
    const d = await apiFetch<ScreeningDetail>(`/aml/screenings/${id}`);
    setDetail(d);
  }

  async function submitReview(screeningId: string, subjectName: string) {
    setReviewing(true);
    try {
      await apiFetch(`/aml/screenings/${screeningId}/review`, {
        method: 'POST',
        body: JSON.stringify({ decision, notes }),
      });
      // If escalated, also file the SAR
      if (decision === 'escalated' && sarSummary.trim()) {
        await apiFetch(`/aml/screenings/${screeningId}/sar`, {
          method: 'POST',
          body: JSON.stringify({
            activityType: sarActivity,
            summary: sarSummary,
            subjectName,
          }),
        }).catch(() => null); // SAR failure non-blocking
      }
      setExpanded(null); setDetail(null); setNotes(''); setSarSummary('');
      await load();
    } finally { setReviewing(false); }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading alerts…</div>;
  if (!alerts.length) return (
    <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
      <CheckCircle className="w-12 h-12 text-green-400" />
      <p className="text-lg font-medium text-gray-600">No pending alerts</p>
      <p className="text-sm">All screenings are clear or have been reviewed.</p>
    </div>
  );

  return (
    <div className="divide-y divide-gray-100">
      {alerts.map(alert => {
        const name = alert.input_full_name
          || [alert.input_first_name, alert.input_last_name].filter(Boolean).join(' ')
          || 'Unknown';
        const isExpanded = expanded === alert.id;

        return (
          <div key={alert.id} className="bg-white">
            <button
              onClick={() => expand(alert.id)}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-50 text-left"
            >
              {alert.status === 'blocked'
                ? <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                : <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              }
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{name}</span>
                  {statusBadge(alert.status)}
                  <span className="text-xs text-gray-400 uppercase tracking-wide">{alert.screening_type}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {alert.user_email || 'No user'} · {alert.match_count} match{alert.match_count !== 1 ? 'es' : ''} · highest {scoreBadge(parseFloat(alert.highest_score))} · {formatDate(alert.screened_at)}
                </div>
              </div>
              {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>

            {isExpanded && detail && (
              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4 space-y-4">
                {/* Match cards */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Matched Entities</p>
                  {detail.matches.map(m => (
                    <div key={m.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="font-medium text-gray-900">{m.matched_name}</div>
                          <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-2">
                            <span>{m.entity_type}</span>
                            <span>·</span>
                            <span>{m.category}</span>
                            <span>·</span>
                            <span>{m.list_name}</span>
                            <span>·</span>
                            <span>src: {m.source_id}</span>
                          </div>
                          {m.programs?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">Programs: {m.programs.join(', ')}</div>
                          )}
                          {m.all_names?.length > 1 && (
                            <div className="text-xs text-gray-400 mt-1">Also known as: {m.all_names.slice(1, 5).join(', ')}</div>
                          )}
                        </div>
                        <div className="text-right space-y-1 flex-shrink-0">
                          <div className="text-xs text-gray-500">Composite {scoreBadge(parseFloat(m.composite_score))}</div>
                          <div className="text-xs text-gray-400">
                            Name {Math.round(parseFloat(m.name_score) * 100)}%
                            · DOB {Math.round(parseFloat(m.dob_score) * 100)}%
                            · Country {Math.round(parseFloat(m.country_score) * 100)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Review panel */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reviewer Decision</p>
                  <div className="flex gap-2 flex-wrap">
                    {['false_positive', 'confirmed_match', 'escalated'].map(d => (
                      <button
                        key={d}
                        onClick={() => setDecision(d)}
                        className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
                          decision === d
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        {d.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Review notes (optional)…"
                    rows={2}
                    className="w-full text-sm border border-gray-200 rounded px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-gray-400"
                  />
                  {/* SAR fields shown when escalating */}
                  {decision === 'escalated' && (
                    <div className="space-y-2 p-3 bg-orange-50 border border-orange-200 rounded">
                      <p className="text-xs font-semibold text-orange-700">SAR Filing Details</p>
                      <select
                        value={sarActivity}
                        onChange={e => setSarActivity(e.target.value)}
                        className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-gray-400"
                      >
                        {SAR_ACTIVITY_TYPES.map(a => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                      <textarea
                        value={sarSummary}
                        onChange={e => setSarSummary(e.target.value)}
                        placeholder="SAR summary (required for escalation)…"
                        rows={3}
                        className="w-full text-sm border border-gray-200 rounded px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                  )}
                  <button
                    onClick={() => submitReview(alert.id, name)}
                    disabled={reviewing || (decision === 'escalated' && !sarSummary.trim())}
                    className="px-4 py-2 bg-gray-900 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
                  >
                    {reviewing ? 'Submitting…' : 'Submit Decision'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SourcesTab() {
  const [sources, setSources]   = useState<AmlSource[]>([]);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ sources: AmlSource[] }>('/aml/sources');
      setSources(data.sources);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function triggerRefresh(sourceId: string) {
    setRefreshing(sourceId);
    try {
      await apiFetch(`/aml/sources/${sourceId}/refresh`, { method: 'POST' });
      // Show optimistic feedback
      setTimeout(() => { setRefreshing(null); load(); }, 2000);
    } catch { setRefreshing(null); }
  }

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading sources…</div>;

  const sanctions     = sources.filter(s => s.category === 'sanctions');
  const pep           = sources.filter(s => s.category === 'pep');
  const adverseMedia  = sources.filter(s => s.category === 'adverse_media');

  function SourceGroup({ title, items }: { title: string; items: AmlSource[] }) {
    return (
      <div>
        <p className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
          {title}
        </p>
        <div className="divide-y divide-gray-100">
          {items.map(src => (
            <div key={src.source_id} className="px-6 py-4 flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900">{src.name}</span>
                  {src.last_status && statusBadge(src.last_status)}
                  <span className="text-xs text-gray-400 uppercase">{src.format}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-3">
                  <span>{src.entity_count.toLocaleString()} entities</span>
                  <span>Last fetched: {formatDate(src.last_fetched_at)}</span>
                  <span>Refresh every {src.fetch_interval_hours}h</span>
                </div>
                {src.last_error && (
                  <div className="text-xs text-red-500 mt-1 truncate max-w-xl">{src.last_error}</div>
                )}
              </div>
              <button
                onClick={() => triggerRefresh(src.source_id)}
                disabled={refreshing === src.source_id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded hover:border-gray-400 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing === src.source_id ? 'animate-spin' : ''}`} />
                {refreshing === src.source_id ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      <SourceGroup title="Sanctions Lists" items={sanctions} />
      <SourceGroup title="PEP Lists (Politically Exposed Persons)" items={pep} />
      {adverseMedia.length > 0 && (
        <SourceGroup title="Adverse Media" items={adverseMedia} />
      )}
    </div>
  );
}

function EntitySearchTab() {
  const [q, setQ]             = useState('');
  const [results, setResults] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);

  async function search() {
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const data = await apiFetch<{ entities: unknown[] }>(`/aml/entities/search?q=${encodeURIComponent(q)}&limit=50`);
      setResults(data.entities);
    } finally { setLoading(false); }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="Search entities by name…"
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
          />
        </div>
        <button
          onClick={search}
          disabled={loading || q.trim().length < 2}
          className="px-4 py-2 bg-gray-900 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {results.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
          {(results as Array<Record<string, unknown>>).map((e, i) => (
            <div key={i} className="px-4 py-3 flex items-center gap-4 flex-wrap">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900">{e.primary_name as string}</span>
                  {statusBadge(e.category as string)}
                  <span className="text-xs text-gray-400">{e.entity_type as string}</span>
                  {!(e.is_active as boolean) && <span className="text-xs text-gray-400">(inactive)</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-2">
                  <span>{e.list_name as string}</span>
                  {(e.programs as string[])?.length > 0 && <span>· {(e.programs as string[]).join(', ')}</span>}
                  {(e.dob as string) && <span>· DOB: {e.dob as string}</span>}
                  {(e.nationalities as string[])?.length > 0 && <span>· {(e.nationalities as string[]).join(', ')}</span>}
                </div>
              </div>
              <span className="text-xs font-mono text-gray-400">{scoreBadge(parseFloat(e.score as string))}</span>
            </div>
          ))}
        </div>
      )}

      {!loading && q && results.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-8">No entities found for "{q}"</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SAR tab
// ─────────────────────────────────────────────────────────────────────────────
const SAR_ACTIVITY_TYPES = [
  { value: 'structuring',       label: 'Structuring' },
  { value: 'money_laundering',  label: 'Money Laundering' },
  { value: 'fraud',             label: 'Fraud' },
  { value: 'sanctions_evasion', label: 'Sanctions Evasion' },
  { value: 'other',             label: 'Other' },
];

function SarTab() {
  const [filings, setFilings]   = useState<SarFiling[]>([]);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = statusFilter ? `/aml/sar?status=${statusFilter}` : '/aml/sar';
      const data = await apiFetch<{ filings: SarFiling[] }>(path);
      setFilings(data.filings);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function updateStatus(id: string, status: string, refNum?: string) {
    setUpdating(id);
    try {
      await apiFetch(`/aml/sar/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, referenceNumber: refNum }),
      });
      await load();
    } finally { setUpdating(null); }
  }

  const sarStatusColors: Record<string, string> = {
    draft:        'bg-gray-100 text-gray-600',
    submitted:    'bg-blue-100 text-blue-700',
    acknowledged: 'bg-yellow-100 text-yellow-700',
    closed:       'bg-green-100 text-green-700',
  };

  if (loading) return <div className="p-6 text-sm text-gray-500">Loading SAR filings…</div>;

  return (
    <div>
      {/* Filter bar */}
      <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
        <span className="text-xs text-gray-500 font-medium">Filter by status:</span>
        {['', 'draft', 'submitted', 'acknowledged', 'closed'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              statusFilter === s
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {filings.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
          <FileText className="w-12 h-12 text-gray-300" />
          <p className="text-lg font-medium text-gray-600">No SAR filings</p>
          <p className="text-sm">File a SAR from the Alerts tab when a confirmed match is escalated.</p>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {filings.map(f => (
          <div key={f.id} className="px-6 py-4 space-y-2">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900">{f.subject_name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${sarStatusColors[f.status] || 'bg-gray-100 text-gray-600'}`}>
                    {f.status}
                  </span>
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                    {SAR_ACTIVITY_TYPES.find(a => a.value === f.activity_type)?.label || f.activity_type}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                  <div>Filed by {f.filer_email} · {formatDate(f.created_at)}</div>
                  {f.amount_involved && (
                    <div>Amount: {parseFloat(f.amount_involved).toLocaleString('en-US', { style: 'currency', currency: f.currency })}</div>
                  )}
                  {f.reference_number && <div>Ref: {f.reference_number}</div>}
                  {f.subject_email && <div>Subject account: {f.subject_email}</div>}
                </div>
                <p className="text-sm text-gray-700 mt-2 line-clamp-3">{f.summary}</p>
              </div>

              {/* Status actions */}
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                {f.status === 'draft' && (
                  <button
                    onClick={() => updateStatus(f.id, 'submitted')}
                    disabled={updating === f.id}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Mark Submitted
                  </button>
                )}
                {f.status === 'submitted' && (
                  <button
                    onClick={() => {
                      const ref = prompt('Enter FinCEN reference number:');
                      if (ref) updateStatus(f.id, 'acknowledged', ref);
                    }}
                    disabled={updating === f.id}
                    className="px-3 py-1.5 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
                  >
                    Acknowledge
                  </button>
                )}
                {['submitted', 'acknowledged'].includes(f.status) && (
                  <button
                    onClick={() => updateStatus(f.id, 'closed')}
                    disabled={updating === f.id}
                    className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded hover:border-gray-500 disabled:opacity-50"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
type Tab = 'alerts' | 'sources' | 'search' | 'sar';

export default function AMLCompliance() {
  const [tab, setTab]     = useState<Tab>('alerts');
  const [stats, setStats] = useState<AmlStats | null>(null);
  const pageTitle = TENANT.id === 'redex' ? `${TENANT.branding.name} Shield — AML Compliance` : 'AML Compliance';

  useEffect(() => {
    apiFetch<AmlStats>('/aml/stats').then(setStats).catch(() => null);
  }, []);

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'alerts',  label: 'Alerts',         icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'sources', label: 'Data Sources',    icon: <Database className="w-4 h-4" /> },
    { id: 'search',  label: 'Entity Search',   icon: <Eye className="w-4 h-4" /> },
    { id: 'sar',     label: 'SAR Filings',     icon: <FileText className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-gray-900" />
          <h1 className="text-xl font-semibold text-gray-900">{pageTitle}</h1>
        </div>

        {/* Stats row */}
        {stats && (
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Sanctions entities:</span>
              <span className="font-semibold">{(stats.entities.sanctions || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">PEP entities:</span>
              <span className="font-semibold">{(stats.entities.pep || 0).toLocaleString()}</span>
            </div>
            {(stats.entities.adverse_media || 0) > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Adverse media:</span>
                <span className="font-semibold">{(stats.entities.adverse_media).toLocaleString()}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Screenings (30d):</span>
              <span className="font-semibold">
                {Object.values(stats.screeningsLast30Days).reduce((a, b) => a + b, 0).toLocaleString()}
              </span>
            </div>
            {stats.pendingAlerts > 0 && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-semibold">{stats.pendingAlerts} pending review</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-0">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.icon}
              {t.label}
              {t.id === 'alerts' && stats?.pendingAlerts ? (
                <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                  {stats.pendingAlerts}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white shadow-sm rounded-b-lg overflow-hidden">
        {tab === 'alerts'  && <AlertsTab />}
        {tab === 'sources' && <SourcesTab />}
        {tab === 'search'  && <EntitySearchTab />}
        {tab === 'sar'     && <SarTab />}
      </div>
    </div>
  );
}
